/* eslint-disable nimbus/prefer-until-destroyed */
import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, skip, take, tap } from 'rxjs/operators';
import { Activity } from '../enums/activity';
import { Availability } from '../enums/availability';
import { GraphPresence } from '../models/presence.model';
import { AuthService } from './auth.service';
import { BaseAuthenticatedPollingService } from './base/base-authenticated-polling-service';
import { GraphApiService } from './graph-api.service';
import { LoggingService } from './logging.service';
import { AssistantHub } from './signalr/assistant.hub';
import { ConfigurationService } from './configuration.service';

@Injectable({
  providedIn: 'root',
})
export class PresenceService extends BaseAuthenticatedPollingService {
  public get defaultTimeoutSec(): number {
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    return this.configurationService.configurationSettings$.value?.presencePollingDefaultTimeoutSec ?? 10;
  }

  public availability$ = new BehaviorSubject<Availability>(undefined);
  public activity$ = new BehaviorSubject<Activity>(undefined);

  public constructor(
    protected readonly authService: AuthService,
    private readonly graphApiService: GraphApiService,
    private readonly hub: AssistantHub,
    private loggingService: LoggingService,
    protected readonly configurationService: ConfigurationService,
  ) {
    super(authService, configurationService);

    this.initializeSubscriptions();
  }

  public initialize(): void {}

  public stopPolling(resetPresence: boolean = true): void {
    super.stopPolling();
    if (resetPresence) {
      this.availability$.next(undefined);
      this.activity$.next(undefined);
    }
  }

  public async pollingAction(): Promise<void> {
    if (this.hub.useSignalrPresence$.value) {
      return null;
    }

    this.graphApiService
      .getMyPresence()
      .pipe(
        catchError((err) => {
          // handle error with retry only if polling used and not signalR
          if (!this.hub.useSignalrPresence$.value) {
            this.handlePollingError();
            this.loggingService.Error(`Error getting presence: ${JSON.stringify(err)}`);
            this.presenceUpdated(null);
          }
          return of(null as GraphPresence);
        }),
        take(1),
        filter((presence) => !!presence),
      )
      .subscribe((presence) => {
        this.presenceUpdated(presence);
        this.handlePollingSuccess();
      });
  }

  private presenceUpdated(presence: GraphPresence): void {
    this.availability$.next(presence?.availability || Availability.PresenceUnknown);
    this.activity$.next(presence?.activity || Activity.PresenceUnknown);
  }

  private initializeSubscriptions(): void {
    this.hub.presence$
      .pipe(filter((_) => this.authService.authenticated$.value && this.hub.useSignalrPresence$.value))
      .subscribe((presence) => {
        this.presenceUpdated(presence);
      });

    combineLatest([
      this.authService.authenticated$.pipe(skip(1), distinctUntilChanged()),
      this.hub.useSignalrPresence$.pipe(distinctUntilChanged()),
    ])
      .pipe(
        tap(([authenticated, useSignalR]) => {
          if (authenticated) {
            this.loggingService.Info(`Presence service switched to ${useSignalR ? `signalR` : `polling`}`);
          }
        }),
        map(([authenticated, useSignalR]) => authenticated && !useSignalR),
        distinctUntilChanged(),
      )
      .subscribe((x) => {
        if (x) {
          this.restartPolling();
        } else {
          this.stopPolling(!this.authService.authenticated$.value);
        }
      });
  }
}
